import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useEventDetail } from '../../hooks/useEventDetail'
import { useAuth } from '../../hooks/useAuth'
import { useSetInterests } from '../../hooks/useSetInterests'
import { useSetFeed } from '../../hooks/useSetFeed'
import { useEventInterest, type EventInterestStatus } from '../../hooks/useEventInterest'
import { useGroups } from '../../hooks/useGroups'
import { useGroupEvents } from '../../hooks/useGroupEvents'
import { useTotemMarking } from '../../hooks/useTotemSlots'
import { useEventPregames } from '../../hooks/usePregames'
import { offersTicketsAndPregames, pregameGoingIds } from '@shared/pregames'
import { useEventTickets } from '../../hooks/useEventTickets'
import { useGroupInterests } from '../../hooks/useGroupInterests'
import { useGroupMemberStatuses } from '../../hooks/useGroupMemberStatuses'
import { useFriendships } from '../../hooks/useFriendships'
import { useUserProfilesWithLoading } from '../../hooks/useUserProfiles'
import { useMergedSelfProfile } from '../../hooks/useMergedSelfProfile'
import { useVenue } from '../../hooks/useVenue'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { createGroupPrompt, rsvpEventPrompt } from '../../lib/guestPrompts'
import { useOneShotTip } from '../../lib/nuxTips'
import { useFrozenValue } from '../../hooks/useFrozenValue'
import { type OneShotTipDismissReason } from '../../components/OneShotTip'
import { track } from '../../lib/analytics'
import { useLineupArtistInterests } from './useLineupArtistInterests'

/**
 * Every subscription the event page and its schedule sub-route share. Both are
 * route-level pages rather than sibling views, so each owns its own mount of
 * this — `useEventDetail`'s module caches make the second mount cheap.
 */
export function useEventPageData(eventId: string) {
  const [searchParams] = useSearchParams()
  // When present (e.g. arriving from a friend invite), the interest overlay is
  // scoped to this one person's picks instead of linked-group members.
  const friendScopeId = searchParams.get('friend')

  const {
    event,
    stages,
    artists,
    artistsLoading,
    lineup,
    allSets,
    setsLoading,
    lineupLoading,
    hasSchedule,
    days,
    defaultDay,
    loading,
    error,
  } = useEventDetail(eventId)

  const { venue } = useVenue(event?.venueId)

  const { user, signedInUid, loading: authLoading } = useAuth()
  const {
    status: eventStatus,
    loading: eventInterestLoading,
    ensureInterested,
    ensureGoing,
    setStatus: setEventStatus,
  } = useEventInterest(signedInUid, eventId)
  // Until auth resolves there is no uid to write against, so an RSVP tapped in
  // that window goes nowhere — and `none` misreports anyone who already answered.
  const eventStatusLoading = authLoading || eventInterestLoading

  const { isGuest, guard, prompt } = useGuestGuard()
  const {
    interestedArtistIds, followHintIds, followCtaArtistIds,
    guardedToggleArtistInterest, guardedMarkArtists, addFollowedArtists,
  } = useLineupArtistInterests(eventId, user?.uid ?? null, ensureInterested, guard, lineup, event?.cancelled === true)

  const setTip = useOneShotTip('set_going')
  const [tipSetId, setTipSetId] = useState<string | null>(null)
  const onMarkedSetInterested = useCallback(
    (setId: string) => {
      if (setTip.shown || tipSetId !== null) return
      setTipSetId(setId)
      track('nux_tip_shown', { tip: 'set_going' })
    },
    [setTip.shown, tipSetId],
  )
  const dismissSetTip = useCallback(
    (via: OneShotTipDismissReason) => {
      setTipSetId(prev => {
        if (prev === null) return prev
        track('nux_tip_dismissed', { tip: 'set_going', via })
        return null
      })
    },
    [],
  )
  const onMarkedSetGoing = useCallback(() => {
    setTip.markShown()
    setTipSetId(null)
    ensureGoing()
  }, [setTip, ensureGoing])

  const {
    interestedSetIds,
    goingSetIds,
    partialRangeBySetId,
    loading: setInterestsLoading,
    cycleSetInterest,
    markSetGoing,
    updateSetPartialRange,
  } = useSetInterests(signedInUid, eventId, {
    onMarked: ensureInterested,
    onMarkedInterested: onMarkedSetInterested,
    onMarkedGoing: onMarkedSetGoing,
    guestLocal: !authLoading,
  })

  const { groups, loading: groupsLoading, createGroup } = useGroups(user?.uid ?? null)
  const { linkedGroups, loading: groupEventsLoading, linkGroup, unlinkGroup, canUnlinkGroup } = useGroupEvents(eventId, user?.uid ?? null, groups)
  const { friendIds, loading: friendsLoading } = useFriendships(user?.uid ?? null)
  const { marking: totemMarking } = useTotemMarking(eventId, user?.uid ?? null, groups, linkedGroups)
  // Anonymous sessions can't host pre-games, keep tickets, or be in anyone's crew;
  // festivals have neither, so nothing on their page subscribes to or marks them.
  const pregameUserId = user && !user.isAnonymous && event && offersTicketsAndPregames(event) ? user.uid : null
  const pregames = useEventPregames(eventId, pregameUserId)
  // Who the viewer can see is going to a pre-game — badged in the people list.
  const pregameGoers = new Set(
    [pregames.mine, ...pregames.invited].flatMap(p => (p ? pregameGoingIds(p) : [])),
  )

  const setFeed = useSetFeed(eventId, user, groups, linkedGroups, friendIds, allSets)
  const { allPosts } = setFeed

  // Whose interests the overlay shows. With ?friend=<uid> it's that one person
  // (the friend-invite "explore their picks" flow); otherwise it's the union of
  // linked-group members and accepted friends — only those with picks for this
  // event actually surface. Self is excluded — own interests come from
  // useSetInterests/useLineupArtistInterests.
  const otherMemberIds = useMemo(() => {
    if (friendScopeId && friendScopeId !== user?.uid) return [friendScopeId]
    const ids = new Set<string>()
    for (const g of linkedGroups) for (const id of g.memberIds) ids.add(id)
    for (const id of friendIds) ids.add(id)
    ids.delete(user?.uid ?? '')
    return [...ids]
  }, [friendScopeId, linkedGroups, friendIds, user?.uid])
  const { setUserIds, setRanges, loading: crewInterestsLoading } = useGroupInterests(otherMemberIds, eventId)
  const tickets = useEventTickets(eventId, pregameUserId, friendScopeId ? null : otherMemberIds)
  // Feed authors on top of the overlay members: a post with an explicit
  // audience can come from a group co-member outside this event's crew, and a
  // post with no name attached to it is worse than no post.
  const profileIds = useMemo(() => {
    const ids = new Set(otherMemberIds)
    for (const p of allPosts) ids.add(p.authorId)
    for (const id of tickets.peopleIds) ids.add(id)
    if (user) ids.add(user.uid)
    return [...ids]
  }, [otherMemberIds, allPosts, tickets.peopleIds, user])
  const { profiles: userProfiles, loading: profilesLoading } = useUserProfilesWithLoading(profileIds)
  const mergedUserProfiles = useMergedSelfProfile(user, userProfiles)

  const { statuses: memberStatuses, loading: statusesLoading } = useGroupMemberStatuses(otherMemberIds, eventId)
  // The statuses subscription reads as settled while its id list is still empty,
  // so the groups and friends it's built from have to have resolved too.
  const peopleLoading = authLoading || statusesLoading ||
    (!friendScopeId && (groupsLoading || groupEventsLoading || friendsLoading))

  const goingUserIds = useMemo(() => {
    const ids = new Set<string>(
      Object.entries(memberStatuses)
        .filter(([, s]) => s === 'going')
        .map(([id]) => id),
    )
    if (!friendScopeId && eventStatus === 'going' && user?.uid) ids.add(user.uid)
    return ids
  }, [memberStatuses, eventStatus, user, friendScopeId])

  const interestedUserIds = useMemo(() => {
    const ids = new Set<string>(
      Object.entries(memberStatuses)
        .filter(([, s]) => s === 'interested')
        .map(([id]) => id),
    )
    if (!friendScopeId && eventStatus === 'interested' && user?.uid) ids.add(user.uid)
    return ids
  }, [memberStatuses, eventStatus, user, friendScopeId])

  // Ranges are account-only; undefined hides the picker and drag-trim.
  const ownRangeUpdate = isGuest ? undefined : updateSetPartialRange

  const handleSetInterest = guard((next: EventInterestStatus) => {
    setEventStatus(next)
  }, rsvpEventPrompt())

  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const handleCreateGroup = useCallback(() => {
    if (isGuest) {
      prompt(createGroupPrompt())
      return
    }
    setCreateGroupOpen(true)
  }, [isGuest, prompt])

  // Frozen: useTrackView keys off the params signature, so a live count would
  // re-fire event_viewed on every interest toggle.
  const viewInterestCount = useFrozenValue(
    interestedSetIds.size + goingSetIds.size,
    !setInterestsLoading,
    user?.uid ?? null,
  )

  return {
    // Event detail
    event, stages, artists, artistsLoading, lineup, allSets, setsLoading,
    lineupLoading, hasSchedule, days, defaultDay, loading, error, venue,
    // Viewer
    user, currentUserId: user?.uid ?? null, signedInUid, friendScopeId, isGuest, guard, prompt,
    // Event RSVP
    eventStatus, eventStatusLoading, handleSetInterest,
    // Artist + set interests
    interestedArtistIds, guardedToggleArtistInterest, guardedMarkArtists,
    followHintIds, followCtaArtistIds, addFollowedArtists,
    interestedSetIds, goingSetIds, setInterestsLoading, cycleSetInterest, markSetGoing,
    partialRangeBySetId, updateSetPartialRange: ownRangeUpdate, setRanges,
    // Social
    groups, createGroup, linkedGroups, linkGroup, unlinkGroup, canUnlinkGroup, friendIds,
    otherMemberIds, setUserIds, crewInterestsLoading, mergedUserProfiles, profilesLoading,
    goingUserIds, interestedUserIds, peopleLoading, totemMarking, setFeed, pregames, pregameUserId, pregameGoers, tickets,
    // Create-group dialog
    createGroupOpen, setCreateGroupOpen, handleCreateGroup,
    // NUX tips
    tipSetId, dismissSetTip,
    // Analytics
    viewInterestCount,
  }
}

export type EventPageData = ReturnType<typeof useEventPageData>
